import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ICONS } from 'src/common/constants';
import Button from '../../Button';
import Col from '../../Col';
import { GenericDialogue } from 'src/components/Dialogue/Dialogue';
import Row from '../../Row';
import Loader from '../../Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { clearSuiteUsers } from 'src/store/actions/suites';
import FontAwesomeIcon from '../../Icon';
import { updateServiceRequest } from 'src/store/actions/serviceRequest';
import Tile from '../../Tile';
import CreatableSelect from 'react-select/creatable';
import { BrandingContext } from 'src/branding/BrandingContextProvider';
import Textarea from '../../Textarea';
import {
  useServiceRequestFormOptions,
  UseServiceRequestFormOptionsReturn,
} from 'src/components/ServiceRequestForm/CustomHooks/useServiceRequestFormOptions';
import { ServiceRequestTag } from 'src/pages/Reporting/CustomHooks/types';

interface ServiceRequestEditFormProps {
  isOpen: boolean;
  serviceRequest: any;
  onClose: () => void;
  onSaved?: () => void;
  handleSaveScheduledAppointment: (
    formData: any,
    serviceRequestId: any
  ) => void;
  [key: string]: any;
}

const ServiceRequestEditForm = ({
  isOpen,
  ...props
}: ServiceRequestEditFormProps) => {
  const dispatch = useDispatch();
  const branding = useContext(BrandingContext);

  const loader = useSelector(selectFormLoader);
  const user = useSelector(selectAuthenticatedUser);

  const {
    types,
    serviceRequestTags,
    loading: optionsLoading,
    error: optionsError,
    fetchFormOptions,
  }: UseServiceRequestFormOptionsReturn = useServiceRequestFormOptions();

  const [issueType, setIssueType] = useState<string>('');
  const [issueTypeId, setIssueTypeId] = useState<string>('');

  const [tags, setTags] = useState<any[]>([]);
  const [description, setDescription] = useState<string>('');

  const onClearSuiteUsers = () => dispatch(clearSuiteUsers() as any);
  const onUpdateServiceRequest = (data: any, context: any) =>
    dispatch(updateServiceRequest(data, context) as any);

  useEffect(() => {
    const { serviceRequest } = props;

    fetchFormOptions();
    onClearSuiteUsers();

    setIssueType(serviceRequest.issueType.name);
    setIssueTypeId(serviceRequest.issueType.id);
    setDescription(serviceRequest.description);

    const tagsData =
      serviceRequest.tags && serviceRequest.tags.length > 0
        ? serviceRequest.tags
        : [];

    setTags(tagsData.map((tag: any) => ({ value: tag.id, label: tag.name })));
  }, []);

  const updateServiceRequestHandler = () => {
    const serviceRequestTagging = tags.map((tag: any) => {
      if (tag.__isNew__) {
        return { name: tag.value };
      }

      return { id: tag.value };
    });

    const data = {
      id: props.serviceRequest.id,
      subject: props.serviceRequest.subject,
      description: description,
      Attachment: '',
      issueType: issueType,
      IssueTypeId: issueTypeId,
      creatorId: user.id,
      tags: serviceRequestTagging,
    };

    onUpdateServiceRequest(data, 'EditServiceRequest');
  };

  const { onClose, onSaved } = props;

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit ${branding?.branding?.text?.serviceRequest ?? 'Service Request'}`}
      description="Update the issue type, tags, and description for this service request."
      maxWidth="100rem"
      maxHeight="75vh"
      minHeight="75vh"
      footer={
        <>
          {loader ? <Loader center={true} /> : null}
          <Button
            color="semantic"
            size="small"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            color="teal"
            size="small"
            label="Save Changes"
            style={{ marginLeft: '1em' }}
            onClick={() => {
              updateServiceRequestHandler();
              onSaved ? onSaved() : onClose();
            }}
          />
        </>
      }
    >
      {optionsLoading ? (
        <Loader center={true} />
      ) : optionsError ? (
        <p>{optionsError}</p>
      ) : (
        <>
          <Row granola>
            {types.map((issueTypeItem: any) => (
              <Col granola key={issueTypeItem.name} xs="6" md="4">
                <Tile
                  icon={
                    <FontAwesomeIcon
                      weight="light"
                      glyph={ICONS[issueTypeItem.name]}
                    />
                  }
                  active={issueTypeItem.name === issueType}
                  onClick={() => {
                    setIssueType(issueTypeItem.name);
                    setIssueTypeId(issueTypeItem.id);
                  }}
                  title={
                    issueTypeItem.name === 'Heating/Cooling'
                      ? 'Heating / Cooling'
                      : issueTypeItem.name
                  }
                  size="tiny"
                />
              </Col>
            ))}
          </Row>
          <label className="textarea__label">Tags</label>
          <CreatableSelect
            placeholder="Add or create tags"
            closeMenuOnSelect={false}
            isClearable
            isMulti
            options={serviceRequestTags.map((item: ServiceRequestTag) => ({
              value: item.id,
              label: item.name,
            }))}
            value={tags}
            onChange={(newValue: any) => setTags(newValue as any[])}
          />
        </>
      )}
      <label className="textarea__label" style={{ marginTop: '.75em' }}>
        Description
      </label>
      <Textarea
        className="textarea"
        value={description}
        onChange={(e: any) => setDescription(e.target.value)}
      />
    </GenericDialogue>
  );
};

export default ServiceRequestEditForm;
